/**
 * Stamps a stored referral code onto the signed-in user's company, once.
 *
 * Attribution is first-stamp-wins and permanent, and the server rejects
 * self-referrals and companies that have already paid before, so this can be
 * called optimistically on any signed-in screen. It runs at most once per
 * browser session and clears the stored code when the server resolves it.
 */
import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { claimStoredCampaign } from '@/lib/campaignAttribution';
import { readStoredReferralCode, readStoredReferralSource, clearStoredReferralCode } from '@/hooks/useReferralProgram';

const SESSION_FLAG = 'cannafax_referral_claim_attempted';

export function useReferralAttributionClaim() {
  const { user } = useAuth();
  const attempted = useRef(false);

  useEffect(() => {
    if (!user || attempted.current) return;
    if (sessionStorage.getItem(SESSION_FLAG)) return;
    void claimStoredCampaign();

    const code = readStoredReferralCode();
    if (!code) return;

    attempted.current = true;
    sessionStorage.setItem(SESSION_FLAG, '1');

    (async () => {
      const { data, error } = await supabase.rpc('claim_referral_attribution', {
        _code: code,
        _source: readStoredReferralSource(),
      });
      const result = data as { ok?: boolean; reason?: string } | null;
      // Clear the code once the server has a final answer on it. A transient
      // network error leaves it in place so a later visit can retry.
      if (!error && (result?.ok || result?.reason !== 'no_company')) {
        clearStoredReferralCode();
      }
    })();
  }, [user]);
}
